import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Card, Table, Badge } from 'react-bootstrap';
import {
  colors,
  radius,
  shadow,
  spacing,
  typography,
} from 'design-system';
import { COURSE_MODULES } from 'constants/driving-elearning.constant';
import { formatTime } from 'utils/commonUtils';
import { buildSummaryRows, activityTimeByCmid, STATUS_VIEW, dataWarningText } from 'utils/completion-summary-view.util';

// Số giờ và trạng thái do backend tính (GET /elearning/my/completion-summary,
// cùng phép tính với nút "Kiểm tra điều kiện"; HV cũ giữ công thức cũ, đã trừ
// giờ loại). Ở đây chỉ hiển thị — không tự tính, không đoán khi thiếu dữ liệu.
function DetailActivityReport({
  activityReport,
  elearningCourses,
  elearningCoursesContents,
  elearningGrades,
  elearningUser,
  completionSummary = null,
  summaryError = null,
  onReloadSummary,
  reloadingSummary = false,
}) {
  const rows = useMemo(
    () => buildSummaryRows(completionSummary, elearningCourses, elearningUser),
    [completionSummary, elearningCourses, elearningUser],
  );

  return (
    <ReportWrap>
      <ReportCard>
        <ReportCardHeader>
          <ReportCardTitle>Kết quả tổng hợp</ReportCardTitle>
          <ReportCardSubtitle>
            {elearningUser?.name} &mdash; {elearningUser?.course?.name}
            {completionSummary?.computedAt && (
              <>
                {' '}&middot; Cập nhật lúc {new Date(completionSummary.computedAt).toLocaleTimeString('vi-VN')}
                {onReloadSummary && (
                  <>
                    {' '}&middot;{' '}
                    <button type='button' className='btn btn-link p-0 align-baseline' onClick={onReloadSummary} disabled={reloadingSummary}>
                      {reloadingSummary ? 'Đang tải...' : 'Tải lại'}
                    </button>
                  </>
                )}
              </>
            )}
          </ReportCardSubtitle>
        </ReportCardHeader>
        <ReportCardBody>
          {dataWarningText(completionSummary) && (
            <div className='alert alert-warning py-2 mb-2'>{dataWarningText(completionSummary)}</div>
          )}
          <StyledTableWrap>
            <Table striped bordered hover size='sm' responsive className='mb-0'>
              <thead>
                <tr>
                  <th>Môn học</th>
                  <th>Thời gian yêu cầu tối thiểu</th>
                  <th>Tổng thời gian đã tích luỹ</th>
                  <th>Kết quả</th>
                </tr>
              </thead>
              <tbody>
                {summaryError ? (
                  <tr>
                    <td colSpan={4}>
                      Không tải được kết quả học tập.{' '}
                      {onReloadSummary && (
                        <button type='button' className='btn btn-link p-0 align-baseline' onClick={onReloadSummary} disabled={reloadingSummary}>
                          Thử lại
                        </button>
                      )}
                    </td>
                  </tr>
                ) : rows.map((row) => {
                  const view = STATUS_VIEW[row.status] || STATUS_VIEW.not_enough_time;
                  return (
                    <tr key={row.key}>
                      <td>{row.name}</td>
                      <td>{formatTime(row.requiredSeconds)}</td>
                      <td>{formatTime(row.countedSeconds)}</td>
                      <td><StatusBadge $tone={view.tone}>{view.label}</StatusBadge></td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </StyledTableWrap>
        </ReportCardBody>
      </ReportCard>
      {activityReport &&
        Object.keys(activityReport).map((key) => {
          const activities = Array.isArray(activityReport?.[key]) ? activityReport[key] : [];

          if(!elearningCourses?.[key]) return null;
          const activityTimes = activityTimeByCmid(completionSummary, key);

          return (
            <ReportCard key={key}>
              <ReportCardHeader>
                <ReportCardTitle>{elearningCourses?.[key]?.fullname}</ReportCardTitle>
              </ReportCardHeader>
              <ReportCardBody>
                <StyledTableWrap>
                  <Table striped bordered hover size='sm' responsive className='mb-0'>
                    <thead>
                      <tr>
                        <th>Tên hoạt động, bài học</th>
                        <th>Loại hoạt động</th>
                        <th>Điểm</th>
                        <th>Điểm để qua</th>
                        <th>Trạng thái</th>
                        <th>Thời gian tích luỹ</th>
                        <th>Hoàn thành lúc</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activities.map((activity) => {
                        const courseContent =
                          elearningCoursesContents?.[key]?.[activity.cmid];
                        const grade =
                          elearningGrades?.[key]?.modules?.[activity.cmid];
                        const timeSpent = activityTimes[activity.cmid];
                        const renderScore = (value, max) => (
                          <ScoreCell>
                            <ScoreValue>{value ?? 0}</ScoreValue>
                            <ScoreMax>/ {max}</ScoreMax>
                          </ScoreCell>
                        );

                        return (
                          <tr key={activity.cmid}>
                            <td>{courseContent?.name}</td>
                            <td>{COURSE_MODULES[activity?.modname]}</td>
                            <td>
                              {activity.modname === 'supervideo'
                                ? renderScore(grade?.finalgrade || 0, grade?.grademax)
                                : activity.modname === 'quiz'
                                ? renderScore(grade?.finalgrade || 0, grade?.grademax)
                                : activity.modname === 'book'
                                ? renderScore(activity?.isoverallcomplete ? 10 : 0, 10)
                                : null}
                            </td>
                            <td>
                              {activity.modname === 'supervideo' ? (
                                <PassMark>{grade?.gradepass || 0}</PassMark>
                              ) : activity.modname === 'quiz' ? (
                                <PassMark>{grade?.gradepass || 0}</PassMark>
                              ) : activity.modname === 'book' ? (
                                <PassMark>10</PassMark>
                              ) : null}
                            </td>
                            <td>
                              {activity.isoverallcomplete ? (
                                <StatusBadge $tone='success'>Đã hoàn thành</StatusBadge>
                              ) : (
                                <StatusBadge $tone='danger'>Chưa hoàn thành</StatusBadge>
                              )}
                            </td>
                            <td>{timeSpent == null ? '—' : formatTime(timeSpent)}</td>
                            <td>{activity?.timecompleted === 0 ? 'N/A' : new Date(activity?.timecompleted * 1000).toLocaleString('vi-VN')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </StyledTableWrap>
              </ReportCardBody>
            </ReportCard>
          );
        })}
    </ReportWrap>
  );
}

/* ─── styled ─── */

const ReportWrap = styled.div`
  margin-top: ${spacing[5]};
`;

const ReportCard = styled(Card)`
  margin-bottom: ${spacing[4]};
  border: 1px solid ${colors.border};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.sm};
  background: ${colors.surface};
  overflow: hidden;
`;

const ReportCardHeader = styled(Card.Header)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[4]};
  background: ${colors.surfaceMuted};
  border-bottom: 1px solid ${colors.border};
`;

const ReportCardTitle = styled.h5`
  margin: 0;
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.semibold};
  color: ${colors.gray900};
`;

const ReportCardSubtitle = styled.span`
  font-size: ${typography.size.sm};
  color: ${colors.gray500};
`;

const ReportCardBody = styled(Card.Body)`
  padding: ${spacing[4]};
`;

// Duplicate StyledTableWrap from ElearningStudentResultPage — single
// consumer outside that page; sharing would add an export surface for
// no real gain. If a third consumer appears, lift to design-system.
const StyledTableWrap = styled.div`
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.sm};
  overflow-y: hidden;
  overflow-x: visible;

  .table-responsive {
    border-radius: inherit;
  }

  table {
    margin-bottom: 0;
    color: ${colors.gray800};
    font-size: ${typography.size.base};
  }

  thead th {
    background: ${colors.surfaceMuted};
    color: ${colors.gray700};
    font-weight: ${typography.weight.semibold};
    font-size: ${typography.size.sm};
    border-color: ${colors.border};
    white-space: nowrap;
  }

  tbody td {
    border-color: ${colors.border};
    vertical-align: middle;
  }
`;

const StatusBadge = styled(Badge)`
  background: ${({ $tone }) =>
    $tone === 'success'
      ? colors.successSoft
      : $tone === 'warning'
      ? colors.warningSoft
      : colors.dangerSoft} !important;
  color: ${({ $tone }) =>
    $tone === 'success'
      ? colors.success
      : $tone === 'warning'
      ? colors.warning
      : colors.danger} !important;
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  padding: ${spacing[1]} ${spacing[2]};
`;

const ScoreCell = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  font-variant-numeric: tabular-nums;
`;

const ScoreValue = styled.span`
  font-weight: ${typography.weight.bold};
  color: ${colors.gray900};
`;

const ScoreMax = styled.span`
  font-weight: ${typography.weight.regular};
  color: ${colors.gray500};
  font-size: ${typography.size.sm};
`;

const PassMark = styled.span`
  color: ${colors.gray500};
  font-variant-numeric: tabular-nums;
`;

export default DetailActivityReport;
